import React, { useState, useEffect } from 'react';
import Sidebar from '../Sidebar';
import { ArrowRight, LayoutDashboard, Megaphone, Palette, Cake, Newspaper } from 'lucide-react';
import HelpCenter from '../HelpCenter';
import ModuloHeader from '../ModuloHeader';
import GeradorArteContratacao from './arte-contratacao/GeradorArteContratacao';
import AniversariantesMes from './aniversariantes/AniversariantesMes';
import GeradorConecta from './conecta/GeradorConecta';
import { perfilNoModulo } from '../../utils/perfisDerivados';
import { PERFIS_MARKETING } from '../../utils/perfisMarketing';

// =====================================================================
// Hub do Módulo MARKETING. Paleta ROSA #db2777.
//
// ⚠️⚠️ O PERFIL EXISTIA HÁ MESES; A TELA, NÃO. `Marketing` está no portal desde
//    o Calendário da Home (gestão de feriados) e ganhou dono em 19/08/2026 —
//    mas quem entrava com ele não encontrava nada seu: não havia rota, hub nem
//    router. Este módulo (16/09/2026) é a casa que faltava.
//
// O QUE ELE GUARDA hoje são duas ferramentas que JÁ EXISTIAM e rodavam FORA do
// portal — arquivo HTML aberto com dois cliques na máquina de uma pessoa:
//   • Gerador de Arte de Contratação — a arte de "estamos contratando" da loja;
//   • Aniversariantes do Mês         — uma arte A4 por filial.
// Elas funcionavam; o que faltava era casa: ninguém mais na rede alcançava, não
// havia versão única, e no dia em que o arquivo sumisse da pasta sumia a
// ferramenta.
//
// ⚠️ QUEM ENTRA: aberto em 18/09/2026 (pedido da Julia) para a LOJA (`Gerente`,
//    `Gerente Plantao`, `Coordenador de Qualidade`) e para as áreas de gente e
//    de operação (`RH`, `Auditoria`, `Operacional`, `Treinamento`), além dos
//    três de origem (`Marketing`, `Diretoria`, `Admin`). A lista é a
//    `PERFIS_MARKETING`, a mesma da rota e do menu.
//    `Diretoria RH` fica de fora — não foi pedida.
//    O `Marketing` e a `Diretoria Op` são perfis DERIVADOS e o `perfilNoModulo`
//    os traduz para `Diretoria` aqui dentro; por isso as listas abaixo não
//    repetem o nome deles.
// =====================================================================

const COR = '#db2777';
const COR_ESCURA = '#9d174d';
const COR_CLARA = '#fdf2f8';
const COR_BORDA = '#fce7f3';

const PERMISSIONS = {
    // ⚠️ A LISTA TEM DE CABER NO `PERFIS` DO SERVIDOR
    //    (`server/marketing/server-marketing.js`). Perfil aqui e fora de lá dá
    //    um card que ABRE e devolve 403 — e o sintoma ("a tela não funciona
    //    para mim") não aponta para a causa.
    //    ⚠️⚠️ ESTA É A TERCEIRA PORTA do módulo, e a mais silenciosa: liberar a
    //    rota e o menu sem mexer aqui dá um hub que ABRE VAZIO — sem card
    //    nenhum, sem erro nenhum. Foi o que se aprendeu com o `DevOps` no
    //    Chamados (ver server/acessos/capacidades.js › a porta de entrada).
    'arte-contratacao': ['Admin', 'Diretoria', ...PERFIS_MARKETING],
    'aniversariantes': ['Admin', 'Diretoria', ...PERFIS_MARKETING],
    // Gerador do Conecta: autoria da newsletter (ato EDITORIAL — a edição aparece
    // na Biblioteca da rede). Mais estreito que as outras artes (que são da loja):
    // só `Diretoria` (perfil derivado que cobre Diretoria/Marketing/Diretoria Op)
    // e `Admin`. ⚠️ TEM DE BATER com o gate do publish em
    // `server/biblioteca/conecta/server-conecta.js` (PERFIS_PUBLICA) — card visível
    // + publish 403 seria o antipadrão que o portal evita.
    'gerador-conecta': ['Admin', 'Diretoria'],
};

const modulesList = [
    {
        id: 'arte-contratacao',
        label: 'Arte de Contratação',
        // ⚠️ Paleta, e não megafone: o megafone é o ícone DO MÓDULO (menu
        //    lateral e Visão Geral). Repeti-lo no card faria a tela parecer o
        //    módulo inteiro.
        icon: Palette,
        color: COR,
        bgColor: COR_CLARA,
        desc: 'A arte de "estamos contratando" da loja: escolha a unidade, marque as vagas e baixe o PNG pronto para postar',
        stat: 'Stories 1080×1920',
    },
    {
        id: 'aniversariantes',
        label: 'Aniversariantes do Mês',
        icon: Cake,
        color: COR,
        bgColor: COR_CLARA,
        desc: 'Uma arte A4 por filial, montada com quem está ativo no cadastro — sem planilha, sem conferir coluna',
        stat: 'PNG · PDF · ZIP',
    },
    {
        id: 'gerador-conecta',
        label: 'Gerador do Conecta',
        icon: Newspaper,
        color: COR,
        bgColor: COR_CLARA,
        desc: 'Monte a newsletter interna: comunicados e pessoas na arte do Conecta, com preview A4 e Baixar PDF',
        stat: 'Newsletter · PDF A4',
    },
];

const MarketingPainel = () => {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : {};
    const userRole = perfilNoModulo(user?.perfil, 'marketing') || '';

    const [isMobile] = useState(window.innerWidth <= 768);
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
    const [activeTab, setActiveTab] = useState('dashboard');
    const [isHelpOpen, setIsHelpOpen] = useState(false);

    const allowedModules = modulesList.filter(
        (mod) => PERMISSIONS[mod.id]?.includes(userRole) || userRole === 'Admin'
    );

    useEffect(() => {
        const handleResize = () => { if (window.innerWidth <= 768) setIsCollapsed(true); };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (activeTab !== 'dashboard' && !(PERMISSIONS[activeTab]?.includes(userRole) || userRole === 'Admin')) {
            setActiveTab('dashboard');
        }
    }, [activeTab, userRole]);

    const OverviewWidgets = () => (
        <div>
            <div style={{
                background: `linear-gradient(135deg, ${COR} 0%, ${COR_ESCURA} 100%)`,
                borderRadius: '16px', padding: '40px', color: '#fff',
                marginBottom: '32px', position: 'relative', overflow: 'hidden',
                boxShadow: '0 10px 15px -3px rgba(219, 39, 119, 0.45)',
            }}>
                <div style={{ position: 'relative', zIndex: 10 }}>
                    <h2 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '12px', marginTop: 0, color: '#fff' }}>
                        Marketing
                    </h2>
                    <p style={{ color: COR_CLARA, fontSize: '18px', maxWidth: '650px', margin: 0, lineHeight: '1.6' }}>
                        As artes que a rede publica — geradas aqui, no padrão do grupo,
                        com os dados que o portal já tem.
                    </p>
                </div>
                <div style={{ position: 'absolute', right: '-30px', top: '-40px', opacity: 0.12 }}>
                    <Megaphone size={250} color="#ffffff" />
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
                {allowedModules.map((mod) => (
                    <div
                        key={mod.id}
                        onClick={() => setActiveTab(mod.id)}
                        style={{
                            backgroundColor: '#fff', borderRadius: '16px', padding: '24px',
                            border: `1px solid ${COR_BORDA}`, display: 'flex', flexDirection: 'column',
                            height: '100%', cursor: 'pointer', position: 'relative', overflow: 'hidden',
                            transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px -2px rgba(0,0,0,0.1)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.05)'; }}
                    >
                        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', backgroundColor: mod.color }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                            <div>
                                <p style={{ fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', color: mod.color, marginBottom: '8px', margin: 0 }}>{mod.stat}</p>
                                <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: 0 }}>{mod.label}</h3>
                            </div>
                            <div style={{ padding: '12px', borderRadius: '12px', backgroundColor: mod.bgColor, color: mod.color }}>
                                <mod.icon size={26} />
                            </div>
                        </div>
                        <div style={{ marginTop: 'auto' }}>
                            <p style={{ fontSize: '14px', color: '#64748b', fontWeight: '500', lineHeight: '1.5', margin: '0 0 16px 0' }}>{mod.desc}</p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '700', color: mod.color }}>
                                <span>Acessar</span> <ArrowRight size={16} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* ⚠️ Diz o que o módulo NÃO faz. As duas telas terminam no arquivo
                baixado — publicar (Instagram, grupo de WhatsApp) continua sendo
                trabalho de quem posta. Sem esta linha, o caminho fácil é achar
                que a tela está incompleta. */}
            <p style={{
                marginTop: 24, padding: '12px 14px', background: COR_CLARA,
                border: `1px solid ${COR_BORDA}`, borderRadius: 10,
                fontSize: 13.5, color: COR_ESCURA, lineHeight: 1.6,
            }}>
                <strong>As duas telas entregam um arquivo</strong> (PNG, PDF ou ZIP) para você baixar
                e publicar onde precisar. O portal não posta nada em rede social nem envia por WhatsApp.
            </p>
        </div>
    );

    const contentBoxStyle = {
        backgroundColor: '#fff', borderRadius: '16px', border: `1px solid ${COR_BORDA}`,
        padding: '32px', minHeight: '75vh', width: '100%',
    };

    const renderContent = () => {
        if (activeTab === 'dashboard') return <OverviewWidgets />;
        if (activeTab === 'arte-contratacao') {
            return <div style={contentBoxStyle}><GeradorArteContratacao /></div>;
        }
        if (activeTab === 'aniversariantes') {
            return <div style={contentBoxStyle}><AniversariantesMes /></div>;
        }
        if (activeTab === 'gerador-conecta') {
            return <div style={contentBoxStyle}><GeradorConecta /></div>;
        }
        return null;
    };

    const getMarginLeft = () => (window.innerWidth <= 768 ? '0px' : (isCollapsed ? '80px' : '260px'));

    return (
        <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8f8f8' }}>
            <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} mostrarBotaoMobile={false} />
            <div style={{ flex: 1, marginLeft: getMarginLeft(), transition: 'margin-left 0.3s ease' }}>

                <ModuloHeader
                    eyebrow="Módulo"
                    titulo="Marketing"
                    cor={COR}
                    onAbrirManual={() => setIsHelpOpen(true)}
                    onAbrirMenu={() => setIsCollapsed(false)}
                    isMobile={isMobile}
                />

                <div style={{ padding: '40px', maxWidth: '1600px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>

                    {/* BARRA DE NAVEGAÇÃO */}
                    <div style={{
                        display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '32px',
                        backgroundColor: '#fff', padding: '8px', borderRadius: '12px',
                        border: `1px solid ${COR_BORDA}`, width: '100%',
                    }}>
                        <button
                            onClick={() => setActiveTab('dashboard')}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                padding: '10px 20px', borderRadius: '8px', border: 'none',
                                fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
                                background: activeTab === 'dashboard' ? `linear-gradient(135deg, ${COR} 0%, ${COR_ESCURA} 100%)` : 'transparent',
                                color: activeTab === 'dashboard' ? '#fff' : '#64748b',
                                boxShadow: activeTab === 'dashboard' ? '0 4px 6px rgba(219, 39, 119, 0.4)' : 'none',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <LayoutDashboard size={18} /> Visão Geral
                        </button>

                        <div style={{ minWidth: '1px', backgroundColor: COR_BORDA, margin: '0 5px' }} />

                        {allowedModules.map((mod) => (
                            <button
                                key={mod.id}
                                onClick={() => setActiveTab(mod.id)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '8px',
                                    padding: '10px 20px', borderRadius: '8px', fontWeight: '600',
                                    cursor: 'pointer', transition: 'all 0.2s',
                                    border: activeTab === mod.id ? `2px solid ${mod.color}` : '2px solid transparent',
                                    backgroundColor: activeTab === mod.id ? mod.bgColor : 'transparent',
                                    color: activeTab === mod.id ? mod.color : '#64748b',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <mod.icon size={18} /> {mod.label}
                            </button>
                        ))}
                    </div>

                    {renderContent()}
                </div>
            </div>

            {/* Manual da Tela — verbetes do módulo (HelpCenter › manualMarketing). */}
            <HelpCenter
                isOpen={isHelpOpen}
                onClose={() => setIsHelpOpen(false)}
                modulo="marketing"
                secaoInicial={activeTab === 'dashboard' ? 'visao-geral-marketing' : `marketing-${activeTab}`}
            />
        </div>
    );
};

export default MarketingPainel;
